import { useState, useMemo } from "react";

function parseValue(value) {
  if (value === undefined || value === null) return "";
  
  if (value && typeof value === "object" && typeof value.toDate === "function") {
    return value.toDate().getTime();
  }
  
  if (value instanceof Date) {
    return value.getTime();
  }
  
  if (typeof value === "string") {
    const dateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dateMatch) {
      return new Date(`${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`).getTime();
    }
    
    const isoMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (isoMatch) {
      return new Date(value).getTime();
    }
  }
  
  return value;
}

export function useSortableTable(data, defaultSortKey = null, defaultDirection = "asc") {
  const [sortConfig, setSortConfig] = useState({
    key: defaultSortKey,
    direction: defaultDirection,
  });

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !data) return data;

    return [...data].sort((a, b) => {
      let aValue = parseValue(a[sortConfig.key]);
      let bValue = parseValue(b[sortConfig.key]);

      if (aValue === "") aValue = sortConfig.direction === "asc" ? Infinity : -Infinity;
      if (bValue === "") bValue = sortConfig.direction === "asc" ? Infinity : -Infinity;

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortDirection = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction;
  };

  return { sortedData, requestSort, getSortDirection, sortConfig };
}

export function SortableHeader({ label, sortKey, onSort, sortDirection, style = {} }) {
  const isActive = sortDirection !== null;
  
  const ariaSort = sortDirection === "asc" 
    ? "ascending" 
    : sortDirection === "desc" 
      ? "descending" 
      : "none";

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSort(sortKey);
    }
  };
  
  return (
    <th
      onClick={() => onSort(sortKey)}
      onKeyDown={handleKeyDown}
      className={`sortable-header ${isActive ? "active" : ""}`}
      style={{ cursor: "pointer", userSelect: "none", ...style }}
      tabIndex={0}
      role="columnheader"
      aria-sort={ariaSort}
      title={`Ordenar por ${label}`}
    >
      <span className="sortable-header-content">
        {label}
        <span className="sort-arrows" aria-hidden="true">
          <span className={`sort-arrow up ${sortDirection === "asc" ? "active" : ""}`}>▲</span>
          <span className={`sort-arrow down ${sortDirection === "desc" ? "active" : ""}`}>▼</span>
        </span>
      </span>
    </th>
  );
}
